import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  AlertTriangle,
  CalendarDays,
  CheckCircle2,
  KeyRound,
  Mail,
  MoreVertical,
  Pencil,
  Plus,
  ShieldCheck,
  Trash2,
  XCircle,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACCESS_LEVELS = [
  { value: 'master_key', label: 'Hoofdsleutel/loper' },
  { value: 'exterior_key', label: 'Buitendeursleutel' },
  { value: 'cabinet_tech_key', label: 'Kast-/technieksleutel' },
  { value: 'alarm_tag_code', label: 'Alarmtag/druppel/code' },
  { value: 'other', label: 'Overig' },
];

const RISK_LEVELS = [
  { value: 'low', label: 'Laag', className: 'bg-emerald-100 text-emerald-700' },
  { value: 'medium', label: 'Middel', className: 'bg-amber-100 text-amber-700' },
  { value: 'high', label: 'Hoog', className: 'bg-red-100 text-red-700' },
];

const STATUSES = [
  { value: 'active', label: 'Actief', className: 'bg-teal-100 text-teal-700' },
  { value: 'returned', label: 'Ingeleverd', className: 'bg-slate-100 text-slate-700' },
  { value: 'lost', label: 'Vermist', className: 'bg-red-100 text-red-700' },
  { value: 'revoked', label: 'Ingetrokken', className: 'bg-orange-100 text-orange-700' },
];

const EMPTY_FORM = {
  key_name: '',
  holder_name: '',
  holder_email: '',
  access_level: 'exterior_key',
  access_description: '',
  full_building_access: false,
  risk_level: 'medium',
  issued_at: '',
  returned_at: '',
  temporary_return_due: '',
  issue_reason: '',
  issued_by: '',
  linked_ministry: '',
  key_agreement_signed: false,
  status: 'active',
  notes: '',
};

const getLabel = (options, value) => options.find(option => option.value === value)?.label || value;
const getClassName = (options, value) => options.find(option => option.value === value)?.className || 'bg-slate-100 text-slate-700';

const KeyManagementPage = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [riskFilter, setRiskFilter] = useState('all');
  const [formData, setFormData] = useState(EMPTY_FORM);

  useEffect(() => {
    fetchRecords();
  }, []);

  const fetchRecords = async () => {
    try {
      const response = await axios.get(`${API}/keys`);
      setRecords(response.data);
    } catch (error) {
      toast.error('Kon sleutelbeheer niet laden');
    } finally {
      setLoading(false);
    }
  };

  const filteredRecords = useMemo(() => {
    return records.filter(record => {
      const statusMatches = statusFilter === 'all' || record.status === statusFilter;
      const riskMatches = riskFilter === 'all' || record.risk_level === riskFilter;
      return statusMatches && riskMatches;
    });
  }, [records, riskFilter, statusFilter]);

  const stats = useMemo(() => {
    return {
      active: records.filter(record => record.status === 'active').length,
      highRisk: records.filter(record => record.risk_level === 'high').length,
      fullAccess: records.filter(record => record.full_building_access).length,
      unsigned: records.filter(record => record.status === 'active' && !record.key_agreement_signed).length,
    };
  }, [records]);

  const resetForm = () => {
    setEditingRecord(null);
    setFormData(EMPTY_FORM);
  };

  const normalizePayload = () => ({
    ...formData,
    holder_email: formData.holder_email || null,
    access_description: formData.access_description || null,
    issued_at: formData.issued_at || null,
    returned_at: formData.returned_at || null,
    temporary_return_due: formData.temporary_return_due || null,
    issue_reason: formData.issue_reason || null,
    issued_by: formData.issued_by || null,
    linked_ministry: formData.linked_ministry || null,
    notes: formData.notes || null,
  });

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formData.key_name || !formData.holder_name) {
      toast.error('Sleutel/toegangsmiddel en houder zijn verplicht');
      return;
    }

    try {
      const payload = normalizePayload();
      if (editingRecord) {
        await axios.put(`${API}/keys/${editingRecord.id}`, payload);
        toast.success('Sleutelregistratie bijgewerkt');
      } else {
        await axios.post(`${API}/keys`, payload);
        toast.success('Sleutelregistratie toegevoegd');
      }
      setDialogOpen(false);
      resetForm();
      fetchRecords();
    } catch (error) {
      toast.error('Actie mislukt');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Weet je zeker dat je deze sleutelregistratie wilt verwijderen?')) return;

    try {
      await axios.delete(`${API}/keys/${id}`);
      toast.success('Sleutelregistratie verwijderd');
      fetchRecords();
    } catch (error) {
      toast.error('Verwijderen mislukt');
    }
  };

  const openEditDialog = (record) => {
    setEditingRecord(record);
    setFormData({
      key_name: record.key_name || '',
      holder_name: record.holder_name || '',
      holder_email: record.holder_email || '',
      access_level: record.access_level || 'exterior_key',
      access_description: record.access_description || '',
      full_building_access: Boolean(record.full_building_access),
      risk_level: record.risk_level || 'medium',
      issued_at: record.issued_at || '',
      returned_at: record.returned_at || '',
      temporary_return_due: record.temporary_return_due || '',
      issue_reason: record.issue_reason || '',
      issued_by: record.issued_by || '',
      linked_ministry: record.linked_ministry || '',
      key_agreement_signed: Boolean(record.key_agreement_signed),
      status: record.status || 'active',
      notes: record.notes || '',
    });
    setDialogOpen(true);
  };

  const updateAccessLevel = (value) => {
    setFormData({
      ...formData,
      access_level: value,
      full_building_access: value === 'master_key' ? true : formData.full_building_access,
      risk_level: value === 'master_key' ? 'high' : formData.risk_level,
    });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 rounded w-56"></div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(item => (
              <div key={item} className="h-24 bg-slate-200 rounded-2xl"></div>
            ))}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="key-management-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Sleutelbeheer</h1>
            <p className="text-slate-500">Beheer sleutels, tags, druppels en alarmtoegang</p>
          </div>
          <Button
            onClick={() => {
              resetForm();
              setDialogOpen(true);
            }}
            className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl"
          >
            <Plus className="w-4 h-4 mr-2" />
            Registratie toevoegen
          </Button>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-5 flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Actief</p>
                <p className="text-2xl font-bold text-slate-900">{stats.active}</p>
              </div>
              <KeyRound className="w-8 h-8 text-teal-600" />
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-5 flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Hoog risico</p>
                <p className="text-2xl font-bold text-slate-900">{stats.highRisk}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-5 flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Volledig gebouw</p>
                <p className="text-2xl font-bold text-slate-900">{stats.fullAccess}</p>
              </div>
              <ShieldCheck className="w-8 h-8 text-indigo-500" />
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-5 flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Zonder verklaring</p>
                <p className="text-2xl font-bold text-slate-900">{stats.unsigned}</p>
              </div>
              <XCircle className="w-8 h-8 text-amber-500" />
            </CardContent>
          </Card>
        </div>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="Filter op status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle statussen</SelectItem>
                  {STATUSES.map(status => (
                    <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={riskFilter} onValueChange={setRiskFilter}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="Filter op risico" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle risiconiveaus</SelectItem>
                  {RISK_LEVELS.map(risk => (
                    <SelectItem key={risk.value} value={risk.value}>{risk.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {filteredRecords.length > 0 ? (
          <div className="grid xl:grid-cols-2 gap-4">
            {filteredRecords.map(record => (
              <Card key={record.id} className="border-0 shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4 min-w-0">
                      <div className="w-12 h-12 rounded-xl bg-teal-50 text-teal-700 flex items-center justify-center shrink-0">
                        <KeyRound className="w-6 h-6" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-semibold text-slate-900 truncate">{record.key_name}</h3>
                        <p className="text-sm text-slate-500 truncate">{record.holder_name}</p>
                        {record.holder_email && (
                          <p className="text-xs text-slate-400 flex items-center gap-1 mt-1">
                            <Mail className="w-3 h-3" />
                            {record.holder_email}
                          </p>
                        )}
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEditDialog(record)}>
                          <Pencil className="w-4 h-4 mr-2" />
                          Bewerken
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDelete(record.id)} className="text-red-600">
                          <Trash2 className="w-4 h-4 mr-2" />
                          Verwijderen
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  <div className="flex flex-wrap gap-2 mt-4">
                    <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${getClassName(STATUSES, record.status)}`}>
                      {getLabel(STATUSES, record.status)}
                    </span>
                    <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${getClassName(RISK_LEVELS, record.risk_level)}`}>
                      Risico {getLabel(RISK_LEVELS, record.risk_level).toLowerCase()}
                    </span>
                    {record.full_building_access && (
                      <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-red-50 text-red-700">
                        Volledig gebouw
                      </span>
                    )}
                    {record.key_agreement_signed ? (
                      <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700">
                        Verklaring getekend
                      </span>
                    ) : (
                      <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-amber-50 text-amber-700">
                        Geen verklaring
                      </span>
                    )}
                  </div>

                  <div className="grid sm:grid-cols-2 gap-3 mt-4 text-sm text-slate-600">
                    <div>
                      <p className="text-xs text-slate-400">Toegangsniveau</p>
                      <p>{getLabel(ACCESS_LEVELS, record.access_level)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Bediening/taak</p>
                      <p>{record.linked_ministry || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Uitgiftedatum</p>
                      <p>{record.issued_at || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Retourdatum tijdelijk</p>
                      <p>{record.temporary_return_due || '-'}</p>
                    </div>
                  </div>

                  {record.access_description && (
                    <p className="mt-4 text-sm text-slate-600 line-clamp-2">{record.access_description}</p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="border-0 shadow-sm">
            <CardContent className="py-12 text-center">
              <KeyRound className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">Geen sleutelregistraties</h3>
              <p className="text-slate-500 mb-4">Leg vast wie welke sleutel, tag, druppel of alarmcode heeft.</p>
              <Button
                onClick={() => {
                  resetForm();
                  setDialogOpen(true);
                }}
                className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl"
              >
                <Plus className="w-4 h-4 mr-2" />
                Eerste registratie toevoegen
              </Button>
            </CardContent>
          </Card>
        )}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingRecord ? 'Sleutelregistratie bewerken' : 'Sleutelregistratie toevoegen'}</DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="key_name">Sleutel/toegangsmiddel *</Label>
                  <Input
                    id="key_name"
                    value={formData.key_name}
                    onChange={(e) => setFormData({ ...formData, key_name: e.target.value })}
                    placeholder="Bijv. Loper kerkgebouw"
                    className="rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="holder_name">Houder *</Label>
                  <Input
                    id="holder_name"
                    value={formData.holder_name}
                    onChange={(e) => setFormData({ ...formData, holder_name: e.target.value })}
                    placeholder="Naam persoon"
                    className="rounded-xl"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="holder_email">E-mail</Label>
                  <Input
                    id="holder_email"
                    type="email"
                    value={formData.holder_email}
                    onChange={(e) => setFormData({ ...formData, holder_email: e.target.value })}
                    placeholder="naam@voorbeeld.nl"
                    className="rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="linked_ministry">Taak/bediening</Label>
                  <Input
                    id="linked_ministry"
                    value={formData.linked_ministry}
                    onChange={(e) => setFormData({ ...formData, linked_ministry: e.target.value })}
                    placeholder="Bijv. kostersteam"
                    className="rounded-xl"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Toegangsniveau</Label>
                  <Select value={formData.access_level} onValueChange={updateAccessLevel}>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ACCESS_LEVELS.map(level => (
                        <SelectItem key={level.value} value={level.value}>{level.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Risiconiveau</Label>
                  <Select value={formData.risk_level} onValueChange={(value) => setFormData({ ...formData, risk_level: value })}>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {RISK_LEVELS.map(level => (
                        <SelectItem key={level.value} value={level.value}>{level.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUSES.map(status => (
                        <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Textarea
                value={formData.access_description}
                onChange={(e) => setFormData({ ...formData, access_description: e.target.value })}
                placeholder="Toegangsomschrijving, bijvoorbeeld: buitendeur, techniekhok en alarm druppel"
                className="rounded-xl resize-none"
                rows={2}
              />

              <div className="grid sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="issued_at">Datum uitgifte</Label>
                  <Input
                    id="issued_at"
                    type="date"
                    value={formData.issued_at}
                    onChange={(e) => setFormData({ ...formData, issued_at: e.target.value })}
                    className="rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="temporary_return_due">Retourdatum tijdelijk</Label>
                  <Input
                    id="temporary_return_due"
                    type="date"
                    value={formData.temporary_return_due}
                    onChange={(e) => setFormData({ ...formData, temporary_return_due: e.target.value })}
                    className="rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="returned_at">Datum inlevering</Label>
                  <Input
                    id="returned_at"
                    type="date"
                    value={formData.returned_at}
                    onChange={(e) => setFormData({ ...formData, returned_at: e.target.value })}
                    className="rounded-xl"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="issued_by">Uitgegeven door</Label>
                  <Input
                    id="issued_by"
                    value={formData.issued_by}
                    onChange={(e) => setFormData({ ...formData, issued_by: e.target.value })}
                    placeholder="Naam verantwoordelijke"
                    className="rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="issue_reason">Reden van uitgifte</Label>
                  <Input
                    id="issue_reason"
                    value={formData.issue_reason}
                    onChange={(e) => setFormData({ ...formData, issue_reason: e.target.value })}
                    placeholder="Bijv. openen gebouw voor diensten"
                    className="rounded-xl"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <label className="flex items-center gap-3 rounded-xl border border-slate-200 p-3 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={formData.full_building_access}
                    onChange={(e) => setFormData({ ...formData, full_building_access: e.target.checked })}
                    className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                  />
                  Toegang tot volledig gebouw
                </label>
                <label className="flex items-center gap-3 rounded-xl border border-slate-200 p-3 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={formData.key_agreement_signed}
                    onChange={(e) => setFormData({ ...formData, key_agreement_signed: e.target.checked })}
                    className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                  />
                  Sleutelverklaring getekend
                </label>
              </div>

              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Interne notities"
                className="rounded-xl resize-none"
                rows={2}
              />

              <div className="rounded-xl bg-amber-50 text-amber-800 p-3 text-sm flex gap-2">
                <CalendarDays className="w-4 h-4 mt-0.5 shrink-0" />
                <p>Een hoofdsleutel/loper wordt standaard als hoog risico en volledige gebouwtoegang behandeld.</p>
              </div>

              <DialogFooter className="gap-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-xl">
                  Annuleren
                </Button>
                <Button type="submit" className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl">
                  {editingRecord ? 'Opslaan' : 'Toevoegen'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default KeyManagementPage;
